/**
 * Lightweight in-memory ring buffer of recent browser-side errors,
 * used by the manual "Report a problem" form (`BugReportButton`) to
 * attach context to user-submitted reports.
 *
 * Also catches `ChunkLoadError` — the only client-side error class
 * we automatically *act on* — and force-reloads the page once per
 * 30s. This fixes the standard "stale tab + redeploy → blank page"
 * race silently without bothering the user.
 *
 * We previously also fired-and-forgot every uncaught error to a
 * backend beacon + admin dashboard, but the data was 100% noise in
 * practice (Script error / safe-link scanners / already-handled
 * ChunkLoadError), so the entire surfacing layer was removed. The
 * code path now does ONLY what's useful: keep a small in-memory
 * buffer for bug reports, and auto-recover stale-bundle chunk loads.
 */
const MAX_ERRORS = 5;
const MAX_MESSAGE_LEN = 400;

const buffer = [];
let installed = false;

function push(kind, message, extra = {}) {
  try {
    const clean = String(message || "").slice(0, MAX_MESSAGE_LEN);
    buffer.push({
      kind,
      message: clean,
      at: new Date().toISOString(),
      ...extra,
    });
    if (buffer.length > MAX_ERRORS) buffer.shift();
  } catch {
    // never let the logger itself throw
  }
}

function maybeReloadOnChunkError(name, detail, stack) {
  // ChunkLoadError = stale bundle, force the browser to fetch the
  // fresh one. Guard against refresh-looping on a genuinely broken
  // deploy with a 30s sessionStorage flag.
  const isChunkErr =
    name === "ChunkLoadError" ||
    /Loading chunk \d+ failed/i.test(detail || "") ||
    /Loading chunk \d+ failed/i.test(stack || "");
  if (!isChunkErr) return;
  try {
    const key = "tt:chunk-reload-attempt";
    const last = Number(sessionStorage.getItem(key) || 0);
    const now = Date.now();
    if (now - last > 30_000) {
      sessionStorage.setItem(key, String(now));
      window.location.reload();
    }
  } catch {
    // sessionStorage blocked (private mode); silent fallback.
  }
}

export function installErrorLog() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Wrap console.error so BugReportButton can attach the last few
  // entries to a user-submitted report.
  const orig = console.error;
  console.error = (...args) => {
    push("console.error", args.map((a) => (a && a.stack ? a.stack : String(a))).join(" "));
    orig.apply(console, args);
  };

  window.addEventListener("error", (e) => {
    // Safari (especially iOS) strips error name/message from `.stack`,
    // so we rebuild a readable message ourselves for the ring buffer.
    const err = e?.error;
    const name = (err && err.name) || "";
    const detail = (err && err.message) || e?.message || "";
    const stack = (err && err.stack) || "";
    const header = name && detail ? `${name}: ${detail}` : (name || detail || "");
    const msg = (header && stack && !stack.startsWith(header))
      ? `${header}\n${stack}`
      : (header || stack || "unknown");
    push("window.error", msg, { source: e?.filename, line: e?.lineno, col: e?.colno });
    maybeReloadOnChunkError(name, detail, stack);
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    const name = (reason && reason.name) || "";
    const detail = (reason && reason.message) || (typeof reason === "string" ? reason : "");
    const stack = (reason && reason.stack) || "";
    const header = name && detail ? `${name}: ${detail}` : (name || detail || "");
    const msg = (header && stack && !stack.startsWith(header))
      ? `${header}\n${stack}`
      : (header || stack || String(reason));
    push("unhandledrejection", msg);
    maybeReloadOnChunkError(name, detail, stack);
  });
}

/** Snapshot of captured errors (newest last) for the bug-report form. */
export function getRecentErrors() {
  return buffer.slice();
}
